// ============================================================
// DEALIN MVP - Header Component (Figma DealinHeader Design 1:1)
// ============================================================

import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bell, MessageCircle, ChevronDown, Menu, X, Search, Briefcase, TrendingUp, Calendar, Settings, User, LogOut, Building2, LayoutDashboard, Store, ClipboardList, Sparkles } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';

function AvatarWithFallback({ src, initials, className = 'w-7 h-7', textClassName = 'text-xs' }: {
  src: string | null;
  initials: string;
  className?: string;
  textClassName?: string;
}) {
  const [failed, setFailed] = useState(false);

  if (src && !failed) {
    return <img src={src} alt="" className={`${className} rounded-full object-cover`} loading="lazy" onError={() => setFailed(true)} />;
  }
  return (
    <div className={`${className} rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center`}>
      <span className={`text-white font-bold ${textClassName}`}>{initials}</span>
    </div>
  );
}

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, profile, logout, isVendor, vendor, unreadMessages, avatarUrl } = useAuth();
  const { unreadCount: unreadNotifications } = useNotifications();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get user display info
  const companyName = vendor?.legal_name || vendor?.name || profile?.company_name || '';
  const userName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || 'Utente';
  const userRole = isVendor ? 'Consulente' : 'Business Analyst';
  const userInitials = userName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase() || 'U';

  // Navigation handlers (Figma style)
  const handleNavigate = (view: string) => {
    switch (view) {
      case 'search':
        navigate('/home');
        break;
      case 'offer':
        navigate('/home');
        break;
      case 'manage':
        navigate('/manage');
        break;
      default:
        navigate('/home');
    }
  };

  // Profile action handlers
  const handleProfileAction = (action: 'company' | 'dashboard' | 'manage-storefront') => {
    setIsProfileOpen(false);
    switch (action) {
      case 'company':
        if (vendor?.id) {
          navigate(`/vendor/${vendor.id}`);
        }
        break;
      case 'dashboard':
        navigate('/manage?page=dashboard');
        break;
      case 'manage-storefront':
        navigate('/manage?page=vetrina');
        break;
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 z-50 sticky top-0 h-14">
      <div className="container mx-auto px-4 h-full">
        <div className="flex items-center justify-between h-full">
          {/* Logo and Navigation */}
          <div className="flex items-center gap-4 sm:gap-6">
            {/* Logo */}
            <Link to={isAuthenticated ? '/home' : '/'} className="flex items-center">
              <img src="/applogo.png" alt="Dealin" className="h-6 w-auto" fetchPriority="high" />
            </Link>
            
            {/* Desktop Navigation - Figma exact style */}
            {isAuthenticated && (
              <nav className="hidden lg:flex items-center gap-0.5">
                <button 
                  onClick={() => handleNavigate('search')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all text-sm font-medium"
                >
                  <Search size={15} />
                  Cerca
                </button>
                <Link
                  to="/assessments"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all text-sm font-medium"
                >
                  <ClipboardList size={15} />
                  Assessment
                </Link>
                <Link
                  to="/aichat"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all text-sm font-medium"
                >
                  <Sparkles size={15} />
                  Assistente AI
                </Link>
                {isVendor && (
                  <Link 
                    to="/manage?page=report"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all text-sm font-medium"
                  >
                    <TrendingUp size={15} />
                    Analytics
                  </Link>
                )}
                <Link
                  to="/manage?page=consulenze"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all text-sm font-medium"
                >
                  <MessageCircle size={15} />
                  Consulenze
                </Link>
                <button 
                  onClick={() => handleNavigate('manage')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-all text-sm font-medium"
                >
                  <Settings size={15} />
                  Gestisci
                </button>
              </nav>
            )}
          </div>

          {/* Right Side Actions - Figma exact style */}
          {isAuthenticated ? (
            <div className="hidden lg:flex items-center gap-1">
              {/* Notifications - Red badge */}
              <Link 
                to="/notifications"
                className="relative p-2 hover:bg-gray-50 rounded-md transition-colors group"
              >
                <Bell size={18} className="text-gray-600 group-hover:text-gray-900" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </Link>

              {/* Messages - Blue badge */}
              <Link 
                to="/chat"
                className="relative p-2 hover:bg-gray-50 rounded-md transition-colors group"
              >
                <MessageCircle size={18} className="text-gray-600 group-hover:text-gray-900" />
                {unreadMessages > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>

              <div className="w-px h-5 bg-gray-200 mx-1.5"></div>

              {/* User Profile Dropdown - Figma exact style */}
              <div className="relative" ref={profileRef}>
                <button 
                  className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded-md transition-colors"
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                >
                  <AvatarWithFallback
                    src={avatarUrl}
                    initials={userInitials}
                    className="w-7 h-7"
                    textClassName="text-xs"
                  />
                  <div className="hidden xl:block text-left">
                    <div className="text-xs font-semibold text-gray-900">{userName}</div>
                    <div className="text-[10px] text-gray-500">{userRole}</div>
                  </div>
                  <ChevronDown size={14} className={`text-gray-500 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Profile Dropdown Menu - Figma exact style */}
                {isProfileOpen && (
                  <div className="absolute right-0 mt-1 w-56 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Account</div>
                      <div className="text-sm font-semibold text-gray-900">{userName}</div>
                      {companyName && <div className="text-xs text-gray-600">{companyName}</div>}
                    </div>
                    <div className="py-0.5">
                      {isVendor && vendor?.id && (
                        <button 
                          onClick={() => handleProfileAction('company')}
                          className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-gray-700 hover:text-gray-900 transition-colors text-xs font-medium"
                        >
                          <Building2 size={14} />
                          La mia azienda
                        </button>
                      )}
                      <button 
                        onClick={() => handleProfileAction('dashboard')}
                        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-gray-700 hover:text-gray-900 transition-colors text-xs font-medium"
                      >
                        <LayoutDashboard size={14} />
                        Dashboard
                      </button>
                      {isVendor && (
                        <button 
                          onClick={() => handleProfileAction('manage-storefront')}
                          className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 text-gray-700 hover:text-gray-900 transition-colors text-xs font-medium"
                        >
                          <Store size={14} />
                          Gestisci vetrina
                        </button>
                      )}
                    </div>
                    <div className="border-t border-gray-100 mt-0.5 pt-0.5">
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-red-50 text-gray-700 hover:text-red-700 transition-colors text-xs font-medium"
                      >
                        <LogOut size={14} />
                        Disconnetti
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="hidden lg:flex items-center gap-2">
              <Link 
                to="/login"
                className="px-3 py-1.5 text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                Accedi
              </Link>
              <Link
                to="/register"
                className="px-5 py-2 bg-[#2563EB] hover:bg-[#1E4FD6] text-white text-sm font-medium rounded-full"
              >
                Registrati
              </Link>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

      </div>

      {/* Mobile Navigation - dropdown panel */}
      {isMenuOpen && (
        <>
          <div className="fixed inset-0 top-14 bg-black/20 z-40 lg:hidden" onClick={() => setIsMenuOpen(false)} />
          <nav className="lg:hidden absolute left-0 right-0 top-14 z-50 bg-white border-b border-gray-200 shadow-lg px-4 py-3 flex flex-col gap-1">
            {isAuthenticated ? (
              <>
                <button 
                  onClick={() => { handleNavigate('search'); setIsMenuOpen(false); }}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <Search size={15} />
                  Cerca Consulenza
                </button>
                <Link
                  to="/assessments"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <ClipboardList size={15} />
                  Assessment
                </Link>
                <Link
                  to="/aichat"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <Sparkles size={15} />
                  Assistente AI
                </Link>
                {isVendor && (
                  <Link 
                    to="/manage?page=report"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                  >
                    <TrendingUp size={15} />
                    Analytics
                  </Link>
                )}
                <Link
                  to="/manage?page=consulenze"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <MessageCircle size={15} />
                  Consulenze
                </Link>
                <button 
                  onClick={() => { handleNavigate('manage'); setIsMenuOpen(false); }}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <Settings size={15} />
                  Gestisci
                </button>
                <div className="border-t border-gray-200 my-1.5"></div>
                <Link 
                  to="/notifications"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <Bell size={15} />
                  Notifiche
                  {unreadNotifications > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </Link>
                <Link 
                  to="/chat"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <MessageCircle size={15} />
                  Messaggi
                  {unreadMessages > 0 && (
                    <span className="ml-auto bg-blue-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </Link>
                <Link 
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  <User size={15} />
                  Profilo
                </Link>
                <div className="border-t border-gray-200 my-1.5"></div>
                <button 
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-red-600 hover:bg-red-50 rounded-md text-sm font-medium"
                >
                  <LogOut size={15} />
                  Disconnetti
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-left px-3 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-md text-sm font-medium"
                >
                  Accedi
                </Link>
                <Link 
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-2 text-center justify-center px-3 py-2.5 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-medium"
                >
                  Registrati
                </Link>
              </>
            )}
          </nav>
        </>
      )}
    </header>
  );
}

export default Header;
